/**
 * Indfrielsesperioden og dokumentationsfristen angives som længder, ikke som datoer.
 * Systemet beregner selv datoerne ud fra afgørelsesdatoen og de perioder, hvor advokaten
 * er udøvende — se FristBeregning i aika-backend. Deponerer advokaten sin beskikkelse,
 * står forløbet stille, og derfor kan datoerne ikke indtastes.
 */

/** Skal matche FristBeregning.TilladtePeriodeLaengder i backenden. */
export const PERIODE_LAENGDER = ["6", "12", "18"] as const;

/** Fast for både påbud og pålæg, jf. FristBeregning.FristLaengdeMaaneder. */
export const FRIST_LAENGDE_MAANEDER = 3;

export const PERIODE_LAENGDE_OPTIONS: LabelValueType[] = PERIODE_LAENGDER.map(
  (maaneder) => ({ label: `${maaneder} måneder`, value: maaneder }),
);

/** Vises hvor en beregnet dato mangler, fordi forløbet står i bero. */
export const I_BERO_TEKST = "I bero";

/**
 * Lægger måneder til en dato og klemmer dagen ned i måneden, når den ikke findes —
 * 30.08 + 6 måneder er 28.02, ikke 02.03. Samme regel som DateOnly.AddMonths i backenden.
 */
const laegMaanederTil = (dato: Date, maaneder: number): Date => {
  const aar = dato.getUTCFullYear();
  const maaned = dato.getUTCMonth() + maaneder;
  const sidsteDagIMaaneden = new Date(
    Date.UTC(aar, maaned + 1, 0),
  ).getUTCDate();
  const dag = Math.min(dato.getUTCDate(), sidsteDagIMaaneden);
  return new Date(Date.UTC(aar, maaned, dag));
};

/**
 * Formaterer resterende udøvende tid som "5 måneder og 27 dage".
 *
 * Månederne tælles på kalenderen fra i dag og frem, ikke som dage divideret med 30. En
 * fast måned på 30 dage er uenig med de datoer, der står lige ved siden af: et påbud på
 * 6 måneder afsagt 26.08.2026 løber til 26.02.2027, altså 184 dage, og med 30-dages
 * måneder ville boksen sige "6 måneder" på en dag, hvor der reelt var 5 måneder og 27
 * dage tilbage — og "6 måneder og 4 dage" på selve afgørelsesdagen.
 *
 * Er advokaten i bero undervejs, er de resterende udøvende dage ikke sammenhængende
 * kalendertid, og så er visningen et skøn — men det er den også i dag, og skønnet her
 * rammer i det mindste rigtigt i det normale tilfælde, hvor advokaten er udøvende hele
 * vejen.
 */
export const formaterResterendeTid = (
  dage: number,
  idag: Date = new Date(),
): string => {
  if (!Number.isFinite(dage) || dage <= 0) return "0 dage";

  const start = new Date(
    Date.UTC(idag.getFullYear(), idag.getMonth(), idag.getDate()),
  );
  const maal = new Date(start.getTime());
  maal.setUTCDate(maal.getUTCDate() + Math.round(dage));

  let maaneder =
    (maal.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (maal.getUTCMonth() - start.getUTCMonth());
  if (laegMaanederTil(start, maaneder).getTime() > maal.getTime()) {
    maaneder -= 1;
  }

  const restDage = Math.round(
    (maal.getTime() - laegMaanederTil(start, maaneder).getTime()) / 86_400_000,
  );

  const maanedsord = maaneder === 1 ? "måned" : "måneder";

  if (maaneder === 0) return `${restDage} dage`;
  if (restDage === 0) return `${maaneder} ${maanedsord}`;

  return `${maaneder} ${maanedsord} og ${restDage} dage`;
};

/**
 * Indfrielsesperioden til visning. Perioden har ingen slutdato, så længe den ikke er
 * udløbet — enten fordi den stadig løber, eller fordi advokaten ikke er udøvende.
 */
export const formaterIndfrielsesperiode = (
  periodeFra: string,
  periodeTil: string | null,
  formatDate: (date: string | null | undefined) => string,
): string =>
  periodeTil
    ? `${formatDate(periodeFra)} – ${formatDate(periodeTil)}`
    : `${formatDate(periodeFra)} – ${I_BERO_TEKST.toLowerCase()}`;

/** Dokumentationsfristen til visning. Ingen frist betyder, at forløbet står i bero. */
export const formaterFrist = (
  frist: string | null | undefined,
  formatDate: (date: string | null | undefined) => string,
): string => (frist ? formatDate(frist) : I_BERO_TEKST);
